import { DataFrame } from '@lib/mobx/dataframe';
import { apiPathToUrl } from '@lib/network/api';
import type { SocialConnectSource, SocialPlatform } from '@lib/social/scheduling/types';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import { computed, runInAction } from 'mobx';

// ── Connections ────────────────────────────────────────────────

export type SocialConnectionStatus = 'active' | 'needs_reauth';

/** `GET /social/connections` row as the API sends it. Fields drift by lifecycle stage, hence the optionals. */
type SocialConnectionWire = {
  _id: string;
  platform: string;
  platformInfo?: {
    externalAccountId?: string;
    displayName?: string;
    avatarUrl?: string;
  };
  status: SocialConnectionStatus;
  createdAt: number;
  updatedAt: number;
};

export type SocialConnection = {
  id: string;
  /** The API's platform id. Unknown values come from connectors newer than this client. */
  platform: string;
  displayName: string | null;
  avatarUrl: string | null;
  status: SocialConnectionStatus;
  /** Unix ms. */
  createdAt: number;
};

const toSocialConnection = (wire: SocialConnectionWire): SocialConnection => {
  if (!wire._id) throw new Error('social connection without _id');
  return {
    id: wire._id,
    platform: wire.platform,
    displayName: wire.platformInfo?.displayName || null,
    avatarUrl: wire.platformInfo?.avatarUrl || null,
    status: wire.status,
    createdAt: wire.createdAt,
  };
};

// ── Store ──────────────────────────────────────────────────────

export class SocialConnectionsStore extends DataFrame<SocialConnection[], SocialConnectionWire[]> {
  // A singleton that outlives logout: a fetch clears the previous user's list
  // rather than showing it under the next user's name until the response lands.
  override keepPreviousDataWhiteFetching = false;

  protected fetch(abortSignal: AbortSignal) {
    return userStore.apiWithAuth<SocialConnectionWire[]>('/social/connections', {
      signal: abortSignal,
    });
  }

  protected override postProcess(wire: SocialConnectionWire[]): SocialConnection[] {
    return wire.map(toSocialConnection);
  }

  /** Newest first. Empty both before the first fetch and when the user has none: key an empty state on `data`, not on this. */
  @computed
  get sortedConnections(): SocialConnection[] {
    return [...(this.data ?? [])].sort((a, b) => b.createdAt - a.createdAt);
  }

  /**
   * Leaves the page to connect (or reconnect) an account. A navigation, not
   * a fetch: the connect route authenticates by the session cookie and ends
   * in a redirect to the platform, which sends the browser back to
   * `returnPath` on this origin (path and query, as `useReturnPath` gives
   * them) with the outcome in the query string, read by
   * `useSocialConnectReturn`. The only way to the connect URL, so every entry
   * point counts in the "started" funnel; `source` is the page it started from.
   */
  connect(platform: SocialPlatform, source: SocialConnectSource, returnPath: string): void {
    trackingStore.socialConnectStarted({ platform, source });
    const returnTo = `${window.location.origin}${returnPath}`;
    window.location.assign(
      apiPathToUrl(`/social/connect/${platform}?returnTo=${encodeURIComponent(returnTo)}`),
    );
  }

  /** Also cancels the account's pending scheduled posts on the API side. */
  async disconnect(id: string): Promise<void> {
    await userStore.apiWithAuth(`/social/connections/${id}`, { method: 'DELETE' });
    runInAction(() => {
      if (this.data) this.data = this.data.filter((c) => c.id !== id);
    });
  }
}

export const socialConnectionsStore = new SocialConnectionsStore();
